import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.js';
import { ApiClient } from '../lib/api.js';

interface Rule { id: string; text: string }
interface Source { title: string; url: string }
interface Playbook {
  version: number;
  hardRules: Rule[];
  softPrinciples: Rule[];
  weights?: Record<string, number>;
  sourceRefs?: Source[];
  notes?: string;
  updatedBy?: string | null;
}

export function AdminRanking() {
  const { getToken } = useAuth();
  const api = new ApiClient(getToken);
  const [me, setMe] = useState<any>(null);
  const [playbook, setPlaybook] = useState<Playbook | null>(null);
  const [history, setHistory] = useState<Playbook[]>([]);
  const [draft, setDraft] = useState<Playbook | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const m = await api.me();
        setMe(m);
        if (!m.isAdmin) return;
        const p = await api.adminGetPlaybook();
        setPlaybook(p.playbook);
        setDraft(p.playbook);
        const h = await api.adminGetPlaybookHistory();
        setHistory(h.history ?? []);
      } catch (e: any) { setErr(e.message); }
    })();
    /* eslint-disable-next-line */
  }, []);

  if (me && !me.isAdmin) return <Navigate to="/app/onboarding" replace />;
  if (!draft) return <div className="loading">Loading playbook…</div>;

  function updateRule(kind: 'hardRules' | 'softPrinciples', idx: number, patch: Partial<Rule>) {
    if (!draft) return;
    setDraft({ ...draft, [kind]: draft[kind].map((r, i) => i === idx ? { ...r, ...patch } : r) });
  }
  function addRule(kind: 'hardRules' | 'softPrinciples') {
    if (!draft) return;
    const id = `rule-${Date.now()}`;
    setDraft({ ...draft, [kind]: [...draft[kind], { id, text: '' }] });
  }
  function deleteRule(kind: 'hardRules' | 'softPrinciples', idx: number) {
    if (!draft) return;
    setDraft({ ...draft, [kind]: draft[kind].filter((_, i) => i !== idx) });
  }

  async function save() {
    if (!draft) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.adminPutPlaybook({
        hardRules: draft.hardRules.filter(r => r.text.trim().length > 0),
        softPrinciples: draft.softPrinciples.filter(r => r.text.trim().length > 0),
        weights: draft.weights,
        sourceRefs: draft.sourceRefs,
        notes: draft.notes,
      });
      setPlaybook(r.playbook);
      setDraft(r.playbook);
      const h = await api.adminGetPlaybookHistory();
      setHistory(h.history ?? []);
      alert(`Saved as v${r.version}. Cache invalidated; new generations use it within 60s.`);
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  }

  async function testOnTopic() {
    setBusy(true); setErr(null);
    try {
      const topic = prompt('Test topic?', 'AI agents for founders');
      if (!topic) { setBusy(false); return; }
      const r = await api.generateTweet(topic);
      alert(r.variants.map(v => `[${v.label}]\n${v.text}`).join('\n\n'));
    } catch (e: any) { setErr(e.message); }
    setBusy(false);
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Ranking Playbook</h1>
        <p className="lede">
          v{playbook?.version} · last updated by {playbook?.updatedBy ?? 'system'}. Rules below are
          injected into every tweet/thread/reply/rewrite prompt. Hard rules override the voice profile on conflicts.
        </p>
      </div>
      {err && <div className="error">{err}</div>}

      <div className="card">
        <div className="button-row between">
          <h3 style={{ margin: 0 }}>Hard rules</h3>
          <button onClick={() => addRule('hardRules')}>+ Add rule</button>
        </div>
        <div className="stack">
          {draft.hardRules.map((r, i) => (
            <div key={i} className="rule-row">
              <input className="rule-id" value={r.id} onChange={e => updateRule('hardRules', i, { id: e.target.value })} placeholder="rule-id" />
              <input value={r.text} onChange={e => updateRule('hardRules', i, { text: e.target.value })} placeholder="NEVER / ALWAYS …" />
              <button className="ghost icon" onClick={() => deleteRule('hardRules', i)} aria-label="Delete rule">×</button>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <div className="button-row between">
          <h3 style={{ margin: 0 }}>Soft principles</h3>
          <button onClick={() => addRule('softPrinciples')}>+ Add principle</button>
        </div>
        <div className="stack">
          {draft.softPrinciples.map((r, i) => (
            <div key={i} className="rule-row">
              <input className="rule-id" value={r.id} onChange={e => updateRule('softPrinciples', i, { id: e.target.value })} placeholder="rule-id" />
              <input value={r.text} onChange={e => updateRule('softPrinciples', i, { text: e.target.value })} placeholder="Soft principle …" />
              <button className="ghost icon" onClick={() => deleteRule('softPrinciples', i)} aria-label="Delete principle">×</button>
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>For You weights</h3>
        <p className="helper">Numeric weights from the X algorithm — appended to the prompt as context (e.g. "reply=13.5, profile_click=12").</p>
        <textarea
          value={JSON.stringify(draft.weights ?? {}, null, 2)}
          onChange={e => {
            try { setDraft({ ...draft, weights: JSON.parse(e.target.value) }); } catch { /* keep typing */ }
          }}
          style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
        />
      </div>

      <div className="card">
        <div className="button-row between">
          <h3 style={{ margin: 0 }}>Source references</h3>
          <button onClick={() => setDraft({ ...draft, sourceRefs: [...(draft.sourceRefs ?? []), { title: '', url: '' }] })}>+ Add source</button>
        </div>
        <div className="stack">
          {(draft.sourceRefs ?? []).map((s, i) => (
            <div key={i} className="rule-row">
              <input value={s.title} onChange={e => {
                const next = [...(draft.sourceRefs ?? [])]; next[i] = { ...next[i], title: e.target.value };
                setDraft({ ...draft, sourceRefs: next });
              }} placeholder="Title" />
              <input value={s.url} onChange={e => {
                const next = [...(draft.sourceRefs ?? [])]; next[i] = { ...next[i], url: e.target.value };
                setDraft({ ...draft, sourceRefs: next });
              }} placeholder="https://…" />
            </div>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>Version notes</h3>
        <textarea value={draft.notes ?? ''} onChange={e => setDraft({ ...draft, notes: e.target.value })} placeholder="What changed in this version and why" />
      </div>

      <div className="card">
        <div className="button-row end">
          <button onClick={testOnTopic} disabled={busy}>Test on a topic</button>
          <button className="primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : `Save as v${(playbook?.version ?? 0) + 1}`}
          </button>
        </div>
      </div>

      {history.length > 0 && (
        <div className="card">
          <h3>History</h3>
          <div className="stack tight">
            {history.map(h => (
              <div key={h.version} className="history-row">
                <strong>v{h.version}</strong>
                <span className="muted small">by {h.updatedBy ?? 'system'}</span>
                <span className="muted small">· {h.hardRules.length} hard · {h.softPrinciples.length} principles</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
